import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { acceptedFiles, ISODateFormat, STALE_TIME } from "@/constants";
import { UserState } from "@/context/User/UserReducer";
import { cn } from "@/lib/util";
import { editCase, getClaimTypes, getLawFirms } from "@/services/Case.service";
import { getInsurers } from "@/services/Insurer.service";
import { getPlatforms } from "@/services/Platform.service";
import { getReporters } from "@/services/Reporter.service";
import { getTowns } from "@/services/Town.service";
import { Case, UpdateCaseParams } from "@/types/case.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { MdOutlineCloudUpload } from "react-icons/md";
import { toast } from "sonner";
import { z } from "zod";
import { Calendar } from "../ui/calendar";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import AttachmentPreview, { FilePreview } from "./AttachmentPreview";

const formSchema = z.object({
	caseno: z.string(),
	issuer: z.number(),
	complainant: z.string(),
	claimtype: z.number(),
	lawfirm: z.number(),
	defendingadvocate: z.string(),
	filingdate: z.date(),
	casedescription: z.string().min(10),
	amountsuedfor: z.number().optional(),
	amountfinancecanoffer: z.number().optional(),
	amounlegalwillpay: z.number().optional(),
	attachment: z.array(z.instanceof(File)).min(0),
	insurerid: z.number(),
	townid: z.number(),
	platformid: z.number(),
	reporterid: z.number(),
});

const EditCase: React.FC<{
	user: UserState | null;
	caseDetails: Case;
}> = ({ user, caseDetails }) => {
	const [open, setOpen] = useState(false);
	const [pickerOpen, setPickerOpen] = useState(false);
	const [uploadedFiles, setUploadedFiles] = useState<FilePreview[]>([]);

	const queryClient = useQueryClient();

	const claimTypes = useQuery({
		queryKey: ["claim_types"],
		queryFn: () => getClaimTypes(user?.accessToken!),
		enabled: !!user?.accessToken,
		staleTime: STALE_TIME,
	});

	const lawFirms = useQuery({
		queryKey: ["law_firms"],
		queryFn: () => getLawFirms(user?.accessToken!),
		enabled: !!user?.accessToken,
		staleTime: 3000,
	});

	const insurers = useQuery({
		queryKey: ["insurers"],
		queryFn: () => getInsurers(user?.accessToken!),
		enabled: !!user?.accessToken,
		staleTime: 3000,
	});

	const towns = useQuery({
		queryKey: ["towns"],
		queryFn: () => getTowns(user?.accessToken!),
		enabled: !!user?.accessToken,
		staleTime: 3000,
	});

	const platforms = useQuery({
		queryKey: ["platforms"],
		queryFn: () => getPlatforms(user?.accessToken!),
		enabled: !!user?.accessToken,
		staleTime: 3000,
	});

	const reporters = useQuery({
		queryKey: ["reporters"],
		queryFn: () => getReporters(user?.accessToken!),
		enabled: !!user?.accessToken,
		staleTime: 3000,
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			issuer: 0,
			caseno: caseDetails.caseno,
			complainant: caseDetails.complainant,
			defendingadvocate: caseDetails.defendingadvocate,
			casedescription: caseDetails.casedescription,
			attachment: [],
			filingdate: new Date(caseDetails.filingdate),
			lawfirm: caseDetails.lawfirmid,
			claimtype: caseDetails.claimtypeid,
			insurerid: caseDetails.insurerid,
			townid: caseDetails.townid,
			platformid: caseDetails.platformid,
			reporterid: caseDetails.reporterid,
			amountsuedfor: caseDetails.amountsuedfor,
			amounlegalwillpay: caseDetails.amounlegalwillpay,
			amountfinancecanoffer: caseDetails.amountfinancecanoffer,
		},
	});

	const caseEdit = useMutation({
		mutationKey: ["case_edit"],
		mutationFn: (tableData: UpdateCaseParams) =>
			editCase(user?.accessToken!, tableData),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["case", caseDetails.caseid.toString()],
				exact: true,
			});
			setOpen(false);
			form.reset();
			toast.success("Case Added");
			caseEdit.reset();
			setUploadedFiles([]);
		},
		onError: (err) => {
			toast.error("Error!", { description: "Error editing case, try again" });
			console.log(err);
		},
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		const tableData: UpdateCaseParams = {
			caseid: caseDetails.caseid,
			issuer: data.issuer,
			caseno: data.caseno,
			complainant: data.complainant,
			claimtype: data.claimtype,
			lawfirm: data.lawfirm,
			defendingadvocate: data.defendingadvocate,
			filingdate: ISODateFormat(data.filingdate),
			publicationdate: ISODateFormat(new Date(Date.now())),
			casedescription: data.casedescription,
			amountsuedfor: data.amountsuedfor,
			amountfinancecanoffer: data.amountfinancecanoffer,
			amounlegalwillpay: data.amounlegalwillpay,
			files: data.attachment,
			insurerid: data.insurerid,
			townid: data.townid,
			platformid: data.platformid,
			reporterid: data.reporterid,
		};
		console.log(tableData);
		caseEdit.mutate(tableData);
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const rawFiles = Array.from(event.target.files || []);
		const files = rawFiles.filter((file) => {
			if (acceptedFiles.includes(file.type)) return true;
			toast.error("File type not supported");
			return false;
		});
		const existingFiles = form.getValues("attachment");
		const allFiles = [...existingFiles, ...files];
		form.setValue("attachment", allFiles);
		const filePreviews = allFiles.map((file) => {
			const filename = file.name;
			const preview = URL.createObjectURL(file);
			return { filename, preview };
		});
		setUploadedFiles(filePreviews);
	};

	const disabled = () => {
		if (caseEdit.isPending) return true;
		if (caseEdit.isSuccess) return true;
		return false;
	};

	const removeFile = (index: number) => {
		const allFiles = form.getValues("attachment");
		const updatedFiles = allFiles.filter((_, i) => i !== index);
		form.setValue("attachment", updatedFiles);

		const updatedPreviews = updatedFiles.map((file) => {
			const filename = file.name;
			const preview = URL.createObjectURL(file);
			return { filename, preview };
		});
		setUploadedFiles(updatedPreviews);
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="default">Edit Case</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle className="text-left font-bold text-2xl">
						Edit Case
					</DialogTitle>
				</DialogHeader>
				{claimTypes.isLoading ? (
					<>Loading Case</>
				) : claimTypes.data ? (
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<h3 className="font-bold text-xl mb-2">Case Details</h3>
							<div className="flex gap-4">
								<FormField
									name="caseno"
									render={({ field }) => (
										<FormItem className="flex-1 mb-2">
											<FormLabel>Case Number</FormLabel>
											<FormControl>
												<Input type="text" {...field} disabled={disabled()} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									name="claimtype"
									render={({ field }) => (
										<FormItem className="mb-2 flex-1">
											<FormLabel>Case Type</FormLabel>
											<FormControl>
												<Select
													onValueChange={(e) => {
														field.onChange(parseFloat(e));
													}}
													disabled={disabled()}
													defaultValue={caseDetails.claimtypeid.toString()}
												>
													<SelectTrigger>
														<SelectValue placeholder="Select an option" />
													</SelectTrigger>
													<SelectContent>
														{claimTypes.data.map((item) => (
															<SelectItem value={item.claimtypeid.toString()}>
																{item.claimtype}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="flex gap-4">
								<FormField
									name="complainant"
									render={({ field }) => (
										<FormItem className="mb-2 flex-1">
											<FormLabel>Plaintiff/Petitioner</FormLabel>
											<FormControl>
												<Input type="text" {...field} disabled={disabled()} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									name="defendingadvocate"
									render={({ field }) => (
										<FormItem className="mb-2 flex-1">
											<FormLabel>Plaintiff/Petitioner Advocate Name</FormLabel>
											<FormControl>
												<Input type="text" {...field} disabled={disabled()} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="flex gap-4">
								<FormField
									control={form.control}
									name="filingdate"
									render={({ field }) => (
										<FormItem className="flex-1 flex flex-col justify-center">
											<FormLabel>Filling Date</FormLabel>
											<Popover open={pickerOpen} onOpenChange={setPickerOpen}>
												<PopoverTrigger asChild>
													<FormControl>
														<Button
															variant={"outline"}
															className={cn(
																"pl-3 text-left font-normal",
																!field.value && "text-muted-foreground"
															)}
														>
															{field.value ? (
																format(field.value, "PPP")
															) : (
																<span>Pick a date</span>
															)}
															<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>
												<PopoverContent className="w-auto p-0" align="start">
													<Calendar
														mode="single"
														selected={field.value}
														onSelect={field.onChange}
														onDayKeyPress={() => setPickerOpen(false)}
														onDayClick={() => setPickerOpen(false)}
														disabled={disabled()}
														initialFocus
													/>
												</PopoverContent>
											</Popover>
											<FormMessage />
										</FormItem>
									)}
								/>
								{lawFirms.isLoading ? (
									<>Loading Law Firms</>
								) : lawFirms.data ? (
									<FormField
										name="lawfirm"
										render={({ field }) => (
											<FormItem className="mb-2 flex-1">
												<FormLabel>Law Firm in Charge</FormLabel>
												<FormControl>
													<Select
														onValueChange={(e) => {
															field.onChange(parseFloat(e));
														}}
														disabled={disabled()}
														defaultValue={caseDetails.lawfirmid.toString()}
													>
														<SelectTrigger>
															<SelectValue placeholder="Select an option" />
														</SelectTrigger>
														<SelectContent>
															{lawFirms.data.map((item) => (
																<SelectItem value={item.lawfirmid.toString()}>
																	{item.lawfirm}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								) : null}
							</div>
							<FormField
								name="casedescription"
								render={({ field }) => (
									<FormItem className="mb-4">
										<FormLabel>Case Summary</FormLabel>
										<FormControl>
											<Textarea {...field} disabled={disabled()} />
										</FormControl>
										<FormMessage className="font-light" />
									</FormItem>
								)}
							/>

							{/* <FormField
								control={form.control}
								name="nextcourtdate"
								render={({ field }) => (
									<FormItem className="flex-1 flex flex-col justify-center">
										<FormLabel>Next Court Date</FormLabel>
										<Popover>
											<PopoverTrigger asChild>
												<FormControl>
													<Button
														variant={"outline"}
														className={cn(
															"pl-3 text-left font-normal",
															!field.value && "text-muted-foreground"
														)}
													>
														{field.value ? (
															format(field.value, "PPP")
														) : (
															<span>Pick a date</span>
														)}
														<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
													</Button>
												</FormControl>
											</PopoverTrigger>
											<PopoverContent className="w-auto p-0" align="start">
												<Calendar
													mode="single"
													selected={field.value}
													onSelect={field.onChange}
													disabled={disabled()}
													initialFocus
												/>
											</PopoverContent>
										</Popover>
										<FormMessage />
									</FormItem>
								)}
							/> */}

							<div className="flex gap-4">
								{insurers.isLoading ? (
									<>Loading Insurers</>
								) : insurers.data ? (
									<FormField
										name="insurerid"
										render={({ field }) => (
											<FormItem className="mb-2 flex-1">
												<FormLabel>Insurer</FormLabel>
												<FormControl>
													<Select
														onValueChange={(e) => {
															field.onChange(parseFloat(e));
														}}
														disabled={disabled()}
														defaultValue={
															caseDetails.insurerid
																? caseDetails.insurerid.toString()
																: undefined
														}
													>
														<SelectTrigger>
															<SelectValue placeholder="Select an option" />
														</SelectTrigger>
														<SelectContent>
															{insurers.data.map((item) => (
																<SelectItem value={item.insurerid.toString()}>
																	{item.insurername}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								) : null}
								{towns.isLoading ? (
									<>Loading Towns</>
								) : towns.data ? (
									<FormField
										name="townid"
										render={({ field }) => (
											<FormItem className="mb-2 flex-1">
												<FormLabel>Town</FormLabel>
												<FormControl>
													<Select
														onValueChange={(e) => {
															field.onChange(parseFloat(e));
														}}
														disabled={disabled()}
														defaultValue={
															caseDetails.townid ? caseDetails.townid.toString() : undefined
														}
													>
														<SelectTrigger>
															<SelectValue placeholder="Select an option" />
														</SelectTrigger>
														<SelectContent>
															{towns.data.map((item) => (
																<SelectItem value={item.townid.toString()}>
																	{item.townname}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								) : null}
							</div>
							<div className="flex gap-4">
								{platforms.isLoading ? (
									<>Loading platforms</>
								) : platforms.data ? (
									<FormField
										name="platformid"
										render={({ field }) => (
											<FormItem className="mb-2 flex-1">
												<FormLabel>Platform</FormLabel>
												<FormControl>
													<Select
														onValueChange={(e) => {
															field.onChange(parseFloat(e));
														}}
														disabled={disabled()}
														defaultValue={
															caseDetails.platformid
																? caseDetails.platformid.toString()
																: undefined
														}
													>
														<SelectTrigger>
															<SelectValue placeholder="Select an option" />
														</SelectTrigger>
														<SelectContent>
															{platforms.data.map((item) => (
																<SelectItem value={item.platformid.toString()}>
																	{item.platformname}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								) : null}
								{reporters.isLoading ? (
									<>Loading reporters</>
								) : reporters.data ? (
									<FormField
										name="reporterid"
										render={({ field }) => (
											<FormItem className="mb-2 flex-1">
												<FormLabel>Reporter</FormLabel>
												<FormControl>
													<Select
														onValueChange={(e) => {
															field.onChange(parseFloat(e));
														}}
														disabled={disabled()}
														defaultValue={
															caseDetails.platformid
																? caseDetails.platformid.toString()
																: undefined
														}
													>
														<SelectTrigger>
															<SelectValue placeholder="Select an option" />
														</SelectTrigger>
														<SelectContent>
															{reporters.data.map((item) => (
																<SelectItem value={item.reporterid.toString()}>
																	{item.reportername}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								) : null}
							</div>
							<>
								<h3 className="font-bold text-xl mb-2 mt-6">Settlement/Damages</h3>
								<div className="flex flex-col md:flex-row gap-4">
									<FormField
										name="amountsuedfor"
										render={({ field }) => (
											<FormItem className="mb-2 flex-1">
												<FormLabel>Amount Being Sued For</FormLabel>
												<FormControl>
													<Input
														type="number"
														placeholder="Enter amount"
														onChange={(e) => field.onChange(e.target.valueAsNumber)}
														disabled={disabled()}
														defaultValue={caseDetails.amountsuedfor.toString()}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									{caseDetails.amountfinancecanoffer > 0 ||
									caseDetails.amounlegalwillpay > 0 ? (
										<>
											<FormField
												name="amountfinancecanoffer"
												render={({ field }) => (
													<FormItem className="mb-2 flex-1">
														<FormLabel>Amount Finance Can Offer</FormLabel>
														<FormControl>
															<Input
																type="number"
																placeholder="Enter amount"
																onChange={(e) => field.onChange(e.target.valueAsNumber)}
																disabled={disabled()}
																defaultValue={
																	caseDetails.amountfinancecanoffer
																		? caseDetails.amountfinancecanoffer.toString()
																		: undefined
																}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
											<FormField
												name="amounlegalwillpay"
												render={({ field }) => (
													<FormItem className="mb-2 flex-1">
														<FormLabel>Amount Legal will Pay</FormLabel>
														<FormControl>
															<Input
																type="number"
																placeholder="Enter amount"
																onChange={(e) => field.onChange(e.target.valueAsNumber)}
																disabled={disabled()}
																defaultValue={
																	caseDetails.amounlegalwillpay
																		? caseDetails.amounlegalwillpay.toString()
																		: undefined
																}
															/>
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>
										</>
									) : null}
								</div>
							</>
							<FormField
								name="attachment"
								render={({ field }) => (
									<FormItem className="mb-2">
										<FormLabel>Attachments</FormLabel>
										<FormLabel className="flex items-center justify-center border-dashed border-2 border-gray-300 rounded-lg p-4 cursor-pointer hover:bg-muted">
											<div className="flex flex-col items-center justify-center gap-2">
												<MdOutlineCloudUpload className="text-4xl" />
												<p>
													<b>Click to upload</b>
												</p>
												<p className="font-light">SVG, PNG, JPG or PDF</p>
											</div>
											<FormControl className="hidden">
												<Input
													type="file"
													multiple
													onChange={(e) => {
														handleFileChange(e);
														// field.onChange(e.target.files);
													}}
													disabled={disabled()}
												/>
											</FormControl>
										</FormLabel>
										<FormMessage />
									</FormItem>
								)}
							/>
							{uploadedFiles.length === 0 ? null : (
								<AttachmentPreview
									files={uploadedFiles}
									deleteFile={removeFile}
									disabled={disabled()}
								/>
							)}
							<Button
								type="submit"
								className="mt-4 w-full"
								disabled={caseEdit.isPending || caseEdit.isSuccess}
							>
								{caseEdit.isPending ? "Updating..." : "Edit Case"}
							</Button>
						</form>
					</Form>
				) : claimTypes.error ? (
					<div className="text-red-500">
						There was an error loading invoice types
					</div>
				) : null}
			</DialogContent>
		</Dialog>
	);
};

export default EditCase;
